/**
 * The single owner of data-table grid height math.
 *
 * Every rule about how tall a table grid renders lives in resolveTableGridHeight:
 * measured-vs-theme fallbacks, the empty-grid overlay reserve, fill/cap clamping,
 * the drill-down drawer budget, row-boundary snapping, and the min-height floor.
 * The DOM shell (sizeDataTableGrid in dashboardHelpers) only gathers measurements
 * and applies the result — it makes no sizing decisions. History shows why: these
 * rules used to be scattered across the clamp branches, the snap call, and the
 * caller, and three consecutive clipping bugs each patched a different corner.
 * New sizing rules belong here, covered by dataTableSizing.test.ts.
 *
 * Pure module — no DOM, no AG Grid imports.
 */

export type TableGridMeasurements = {
  /** Measured .ag-header height; 0 when not yet laid out. */
  measuredHeaderHeight: number;
  /** Theme header height (getSizesForCurrentTheme) — fallback when unmeasured. */
  themeHeaderHeight: number;
  /** Theme row height (getSizesForCurrentTheme) — fallback when rows are unmeasured. */
  themeRowHeight: number;
  /** Displayed data rows, post quick-filter. */
  dataRowCount: number;
  /**
   * Measured .ag-center-cols-container height; 0 when unmeasured. Preferred over
   * `dataRowCount × themeRowHeight`: themes can render rows off-spec and a few px
   * of error per row accumulates into a grid that clips its last rows while
   * leaving a blank band below.
   */
  measuredRowsHeight: number;
  /** Pinned bottom ("Total") rows. */
  pinnedBottomRowCount: number;
  /** Measured .ag-floating-bottom height; 0 when unmeasured. */
  measuredPinnedBottomHeight: number;
  /** Measured .ag-body-horizontal-scroll height; 0 when there is no horizontal overflow. */
  horizontalScrollHeight: number;
  /**
   * True when the horizontal scrollbar is an overlay — AG Grid marks its fake
   * scrollbar `ag-scrollbar-invisible` and positions it absolutely OVER the body
   * (macOS "automatically"/"when scrolling" modes). The element still measures
   * ~16px tall, but it consumes no layout flow height.
   */
  horizontalScrollIsOverlay: boolean;
};

export type TableGridConstraint =
  /**
   * Standalone dashboard blocks: clamp to the space the block's layout row gives
   * us (container minus the "N rows" footer). availableHeight <= 0 means the
   * container hasn't laid out yet — size to content rather than collapsing.
   */
  | { mode: "fill"; availableHeight: number }
  /**
   * Modal drill-downs: grow to content up to a viewport-based cap, further capped
   * by the drawer's visible budget when it could be measured (null when the
   * drawer isn't measurable — hidden, mid-animation, or no max-height ancestor).
   */
  | { mode: "cap"; maxHeight: number; drawerBudget: number | null };

/**
 * The card-level height pair a table block reports to the layout after each
 * sizing pass. Both are full card heights (title/description chrome + grid +
 * footer + any content below the table + card bottom padding).
 */
export type TableContentHeightReport = {
  /**
   * Card height at the grid's UNCLAMPED content height — what the card would
   * need to show every row. Drives auto-collapse under short content and
   * grow-to-fit near misses.
   */
  natural: number;
  /**
   * Card height hugging the grid as actually rendered (clamped + snapped to a
   * whole row). When content overflows beyond the grow tolerance, the row
   * collapses to this so the snap remainder (< 1 row) doesn't sit as a dead
   * band above the card's bottom padding. Equals `natural` when content fits.
   */
  hugged: number;
};

/**
 * Card chrome around the grid, all measured by the DOM shell: content above the
 * table container, the built-in row-count footer, content below the container
 * (custom render JS appends legend notes there — see chromeBelow), and the
 * card's bottom padding.
 */
export type TableCardChromeMeasurements = {
  /** Container top minus card top: title, description, anything above the table. */
  chromeAbove: number;
  /**
   * Card bottom minus container bottom minus card bottom padding: content custom
   * render JS appended below the table container (e.g. a legend note). Leaving
   * this out under-reports the card height, and the layout row then squeezes the
   * grid by exactly this much — report → resize → re-report oscillation (visible
   * as endless flicker).
   */
  chromeBelow: number;
  /** Measured .coeff-data-table-footer height; 0 when not rendered. */
  footerHeight: number;
  /** Card's computed padding-bottom. */
  cardPaddingBottom: number;
};

/**
 * Assembles the card-level height report from the resolved grid heights and the
 * measured card chrome. `natural` uses the unclamped content height (drives
 * auto-collapse / grow-to-fit), `hugged` uses the grid as actually rendered
 * (clamped + row-snapped).
 */
export function computeTableContentHeightReport(
  chrome: TableCardChromeMeasurements,
  grid: ResolvedTableGridHeight
): TableContentHeightReport {
  const chromeTotal =
    chrome.chromeAbove + chrome.chromeBelow + chrome.footerHeight + chrome.cardPaddingBottom;
  return {
    natural: Math.ceil(chromeTotal + grid.contentHeight),
    hugged: Math.ceil(chromeTotal + grid.gridHeight),
  };
}

export type ResolvedTableGridHeight = {
  /** Height to write to the grid container. */
  gridHeight: number;
  /**
   * Natural (unclamped) content height — reported upward so the block's layout
   * row can auto-collapse or grow-to-fit (computeEffectiveRowHeight).
   */
  contentHeight: number;
};

/**
 * When the grid is clamped below its content height, the clamp almost never lands
 * on a row boundary, so the row at the fold renders sliced in half — which reads
 * as a rendering bug even though the grid scrolls internally. Snap the data-rows
 * area down to a whole number of rows so the cut edge is always a row boundary.
 * `perRowHeight` is the average laid-out row height (measured, not the theme
 * constant — themes can render rows off-spec and the error accumulates); pass 0
 * to no-op (no data rows to snap against).
 */
export function snapClampedGridHeightToRowBoundary(
  clampedHeight: number,
  contentHeight: number,
  chrome: {
    headerHeight: number;
    perRowHeight: number;
    pinnedBottomHeight: number;
    horizontalScrollHeight: number;
  }
): number {
  if (clampedHeight >= contentHeight || chrome.perRowHeight <= 0) {
    return clampedHeight;
  }
  const rowsSpace =
    clampedHeight - chrome.headerHeight - chrome.pinnedBottomHeight - chrome.horizontalScrollHeight;
  const wholeRows = Math.floor(rowsSpace / chrome.perRowHeight);
  if (wholeRows < 1) {
    return clampedHeight;
  }
  return Math.min(
    clampedHeight,
    chrome.headerHeight +
      wholeRows * chrome.perRowHeight +
      chrome.pinnedBottomHeight +
      chrome.horizontalScrollHeight
  );
}

export function resolveTableGridHeight(
  m: TableGridMeasurements,
  constraint: TableGridConstraint
): ResolvedTableGridHeight {
  const headerHeight = m.measuredHeaderHeight > 0 ? m.measuredHeaderHeight : m.themeHeaderHeight;

  // Reserve a scrollbar lane only for an in-flow scrollbar. An overlay
  // scrollbar consumes no flow height — counting its measured height pushes the
  // row snap half a row past the fold, slicing the last visible row. The
  // floating thumb briefly covering the last row's bottom while scrolling is
  // native macOS behavior; reserve no lane for it.
  const horizontalScrollHeight = m.horizontalScrollIsOverlay ? 0 : m.horizontalScrollHeight;

  // An empty grid still needs vertical room for AG Grid's centered "No Rows To
  // Show" overlay; sizing to the header alone clips it. Reserve a few rows' worth.
  const rowsHeight =
    m.dataRowCount === 0
      ? 3 * m.themeRowHeight
      : m.measuredRowsHeight > 0
      ? m.measuredRowsHeight
      : m.dataRowCount * m.themeRowHeight;

  const pinnedBottomHeight =
    m.pinnedBottomRowCount > 0
      ? m.measuredPinnedBottomHeight > 0
        ? m.measuredPinnedBottomHeight
        : m.pinnedBottomRowCount * m.themeRowHeight
      : 0;

  const contentHeight = headerHeight + rowsHeight + pinnedBottomHeight + horizontalScrollHeight;
  // Never collapse below a single header + row so an empty/one-row table still
  // reads as a table.
  const minHeight = headerHeight + m.themeRowHeight + pinnedBottomHeight;

  let clampedHeight: number;
  if (constraint.mode === "cap") {
    clampedHeight = Math.min(contentHeight, constraint.maxHeight);
    if (constraint.drawerBudget !== null && constraint.drawerBudget > 0) {
      clampedHeight = Math.min(clampedHeight, constraint.drawerBudget);
    }
  } else {
    clampedHeight =
      constraint.availableHeight > 0
        ? Math.min(contentHeight, constraint.availableHeight)
        : contentHeight;
  }

  // Whichever mode clamped us, never leave a half-row at the fold.
  const perRowHeight =
    m.dataRowCount > 0
      ? m.measuredRowsHeight > 0
        ? m.measuredRowsHeight / m.dataRowCount
        : m.themeRowHeight
      : 0;
  const snappedHeight = snapClampedGridHeightToRowBoundary(clampedHeight, contentHeight, {
    headerHeight,
    perRowHeight,
    pinnedBottomHeight,
    horizontalScrollHeight,
  });

  return {
    gridHeight: Math.max(minHeight, snappedHeight),
    contentHeight: Math.max(minHeight, contentHeight),
  };
}
